import React, { useState } from 'react'
import { Field, Form, Formik } from 'formik'
import * as Yup from 'yup'
import { useHistory, useLocation } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { isEmpty } from 'lodash'
import { v4 as uuid } from 'uuid'

import CategoryItems from 'components/DisplayCategories/CategoryItems'
import Modal from 'components/Modal'
import TextBox from 'components/TextBox'
import BoxColor from 'components/BoxColor'
import AssignedItemsButton from 'components/AssignedItemsButton'

import {
  getCurrentDisplayCategory,
  getIsLoadingDisplayCategories,
} from 'store/DisplayCategories/selectors'
import {
  updateDisplayCategories,
  createDisplayCategory,
} from 'store/DisplayCategories/actions'
import {
  getItems,
  getIsItemsLoading,
  getCurrentAssignedDisplayCategoriesItemsUuid,
} from 'store/Items/selector'
import { getCurrentVenueUuid } from 'store/Venues/selector'
import { updateGlobalItem } from 'store/Items/actions'
import { transformItemsToUpdateInCategory } from 'utils/categories/transformItemsToUpdateInCategory'
import { transformTextToHexFormat } from 'utils/transformTextToHexFormat'
import { validationRegex, errorMessages } from 'utils/validations'
import { validateHexColor } from 'utils/validateHexColor'

const CategoryForm = () => {
  const [showValidationErrors, setShowValidationErrors] =
    useState(false)
  const [formStep, setFormStep] = useState(1)

  const history = useHistory()
  const dispatch = useDispatch()
  const { state: urlQueryParamsState } = useLocation()
  const categoryUuid = urlQueryParamsState?.categoryUuid
  const isNewCategory = categoryUuid === 'new'

  const currentVenueUuid = useSelector(getCurrentVenueUuid)

  const isCategoriesLoading = useSelector(
    getIsLoadingDisplayCategories
  )
  const isItemsLoading = useSelector(getIsItemsLoading)
  const isLoading = isCategoriesLoading || isItemsLoading

  const currentCategory = useSelector((state) =>
    getCurrentDisplayCategory(state, categoryUuid)
  )
  const allItems = useSelector(getItems)
  const assaignedItemsArrayUuid = useSelector((state) =>
    getCurrentAssignedDisplayCategoriesItemsUuid(state, categoryUuid)
  )

  const validationSchema = Yup.object().shape({
    name: Yup.string().required('Required'),
    canopyDisplayName: Yup.string().required('Required'),
    color: Yup.string()
      .matches(
        validationRegex.sixDigitHexColor,
        errorMessages.hexColor
      )
      .nullable(),
  })

  const initialValues = {
    name: currentCategory?.name ?? '',
    canopyDisplayName: currentCategory?.canopyDisplayName ?? '',
    color: currentCategory?.color ?? null,
    items: assaignedItemsArrayUuid,
  }

  const disableUpdateButton = (dirty, errors) => {
    const disableButton =
      !dirty || (showValidationErrors && !isEmpty(errors))
    return disableButton
  }

  const onCloseModal = () =>
    history.push({ state: { categoryUuid: null } })

  const modalTitle = isNewCategory
    ? 'Add a Display Category'
    : 'Display Category'

  const modalStepTitle = formStep === 1 ? modalTitle : 'Items'

  const onSubmit = (values) => {
    const { name, canopyDisplayName, color, items } = values
    const categoryParams = {
      name,
      canopyDisplayName,
      color: isEmpty(color) ? null : color,
      uuid: currentCategory?.uuid ?? uuid(),
      venueUuid: currentVenueUuid,
    }

    if (isNewCategory) {
      const newCategoryParams = {
        ...categoryParams,
        productType: 'Food',
      }
      dispatch(createDisplayCategory([newCategoryParams]))
        .then((response) => {
          const itemsParams = transformItemsToUpdateInCategory(
            initialValues.items,
            allItems,
            items,
            response.payload[0].uuid
          )

          if (!isEmpty(itemsParams)) {
            return dispatch(updateGlobalItem(itemsParams))
          }
        })
        .then(() => history.push({ state: { categoryUuid: null } }))
    } else {
      dispatch(updateDisplayCategories([categoryParams]))
        .then(() => {
          const itemsParams = transformItemsToUpdateInCategory(
            initialValues.items,
            allItems,
            items,
            currentCategory?.uuid
          )

          if (!isEmpty(itemsParams)) {
            return dispatch(updateGlobalItem(itemsParams))
          }
        })
        .then(() => history.push({ state: { categoryUuid: null } }))
    }
  }

  return (
    <Modal
      title={modalStepTitle}
      onClose={onCloseModal}
      className="-narrow"
      isReturnIcon={formStep === 2}
      onClickReturn={() => setFormStep(1)}
      onRequestClose={onCloseModal}
      isLoading={isLoading}
    >
      <div className="modal-body">
        <Formik
          enableReinitialize
          initialValues={initialValues}
          onSubmit={onSubmit}
          validationSchema={validationSchema}
        >
          {({ dirty, errors, values, setFieldValue }) => (
            <Form className="category-item-details">
              {formStep === 1 && (
                <div className="item-settings">
                  <Field
                    component={TextBox}
                    label="Name"
                    name="name"
                    className="category-item-details__text-field"
                  />
                  <Field
                    component={TextBox}
                    label="Display Name"
                    name="canopyDisplayName"
                    className="category-item-details__text-field"
                  />
                  <div className="category-item-details__color-wrapper">
                    <TextBox
                      label="POS Hex Color"
                      name="color"
                      className="category-item-details__color-wrapper__text-field"
                      value={values?.color}
                      onChange={(event) =>
                        setFieldValue(
                          'color',
                          transformTextToHexFormat(event.target.value)
                        )
                      }
                      showValidationErrors={showValidationErrors}
                    />
                    <BoxColor
                      className="category-item-details__color-wrapper__box-color"
                      color={validateHexColor(values?.color)}
                    />
                  </div>
                  <h2 className="category-item-details__items-header">
                    Items
                  </h2>
                  <div className="category-item-details__autocomplete">
                    <AssignedItemsButton
                      label="Select Items"
                      assignedItems={values?.items?.length}
                      handleClick={() => setFormStep(2)}
                    />
                  </div>
                  <div className="btn-wrapper">
                    <button
                      className="btn -update"
                      disabled={disableUpdateButton(dirty, errors)}
                      type="submit"
                      onClick={() => setShowValidationErrors(true)}
                    >
                      Save
                    </button>
                  </div>
                </div>
              )}
              {formStep === 2 && (
                <CategoryItems
                  goBack={() => setFormStep(1)}
                  allItems={allItems}
                />
              )}
            </Form>
          )}
        </Formik>
      </div>
    </Modal>
  )
}

export default CategoryForm
